import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import CompanyModal from './CompanyModal';
import CompanyViewModal from './CompanyViewModal';
import apiClient from '../../assets/scripts/services/apiClient';
import authService from '../../assets/scripts/auth/authService';

const CompanyManagement = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [modalTitle, setModalTitle] = useState('Create Company');
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);

  useEffect(() => {
    checkAuthAndLoadCompanies();
  }, []);

  const checkAuthAndLoadCompanies = async () => {
    const isAuthenticated = await authService.requireAuth();
    if (!isAuthenticated) {
      return;
    }
    loadCompanies();
  };

  const loadCompanies = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.get('/organizations');
      setCompanies(data || []);
    } catch (err) {
      console.error('Error loading companies:', err);
      setError('Failed to load companies');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCompany = () => {
    setSelectedCompany(null);
    setModalTitle('Create New Company');
    setModalOpen(true);
  };

  const handleEditCompany = (company) => {
    setSelectedCompany(company);
    setModalTitle('Edit Company');
    setModalOpen(true);
  };

  const handleDeleteCompany = async (companyId) => {
    if (!confirm('Are you sure you want to delete this company? This action cannot be undone.')) {
      return;
    }

    try {
      await apiClient.delete(`/organizations/${companyId}`);
      await loadCompanies();
      showNotification('Company deleted successfully', 'success');
    } catch (err) {
      console.error('Error deleting company:', err);
      showNotification('Failed to delete company', 'error');
    }
  };

  const handleSaveCompany = async (formData) => {
    const detail = {};
    Object.keys(formData).forEach(key => {
      if (formData[key].trim()) {
        detail[key] = formData[key].trim();
      }
    });

    try {
      if (selectedCompany) {
        // Update existing company using PATCH for partial updates
        await apiClient.patch(`/organizations/${selectedCompany.id}`, { detail });
        showNotification('Company updated successfully', 'success');
      } else {
        // Create new company
        await apiClient.post('/organizations', { detail });
        showNotification('Company created successfully', 'success');
      }
      
      setModalOpen(false);
      await loadCompanies();
    } catch (err) {
      console.error('Error saving company:', err);
      throw err; // Let modal handle the error
    }
  };

  const showNotification = (message, type = 'info') => {
    // For now, using alert. Can be replaced with a proper notification system
    if (type === 'error') {
      alert('Error: ' + message);
    } else {
      alert('Success: ' + message);
    }
  };

  const handleViewCompany = (company) => {
    setSelectedCompanyId(company.id);
    setViewModalOpen(true);
  };

  if (loading) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  return (
    <div className="company-management">
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="peers ai-c jc-sb fxw-nw mB-30">
              <div className="peer">
                <h4 className="c-grey-900 mT-10 mB-0">Company Management</h4>
              </div>
              <div className="peer">
                <button 
                  className="btn btn-primary" 
                  onClick={handleCreateCompany}
                >
                  <i className="ti-plus mR-5"></i>Create New Company
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <div className="row">
          <div className="col-12">
            <div className="bgc-white bd bdrs-3 p-20 mB-20">
              <h5 className="c-grey-900 mB-20">Companies</h5>
              <div className="table-responsive">
                <table className="table table-striped table-bordered">
                  <thead>
                    <tr>
                      <th>Organization Name</th>
                      <th>Organization Type</th>
                      <th>License Type</th>
                      <th>Industry</th>
                      <th>Website</th>
                      <th>Phone</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {companies.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="text-center">
                          No companies found. Create your first company to get started.
                        </td>
                      </tr>
                    ) : (
                      companies.map(company => {
                        const detail = company.detail || {};
                        return (
                          <tr key={company.id}>
                            <td>{detail.org_name || 'N/A'}</td>
                            <td>{detail.org_type || 'N/A'}</td>
                            <td>{detail.license_type || 'N/A'}</td>
                            <td>{detail.industry || 'N/A'}</td>
                            <td>
                              {detail.website ? (
                                <a href={detail.website} target="_blank" rel="noopener noreferrer">
                                  {detail.website}
                                </a>
                              ) : (
                                'N/A'
                              )}
                            </td>
                            <td>{detail.phone || 'N/A'}</td>
                            <td>
                              <button 
                                className="btn btn-sm btn-primary me-2"
                                onClick={() => handleViewCompany(company)}
                              >
                                <i className="ti-eye"></i> View
                              </button>
                              <button 
                                className="btn btn-sm btn-secondary me-2"
                                onClick={() => handleEditCompany(company)}
                              >
                                <i className="ti-pencil"></i> Edit
                              </button>
                              <button 
                                className="btn btn-sm btn-danger"
                                onClick={() => handleDeleteCompany(company.id)}
                              >
                                <i className="ti-trash"></i> Delete
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CompanyModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSaveCompany}
        company={selectedCompany}
        title={modalTitle}
      />

      <CompanyViewModal
        isOpen={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        companyId={selectedCompanyId}
      />
    </div>
  );
};

// Export component
export default CompanyManagement;

// Auto-mount if element exists
export const initCompanyManagement = () => {
  const mountElement = document.getElementById('company-management-root');
  if (mountElement) {
    const root = ReactDOM.createRoot(mountElement);
    root.render(<CompanyManagement />);
  }
};
